import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  combinedPurchaseExceedsDailyCap,
  formatCombinedLimitExceededMessage,
  purchaseDeltasFromCartLines,
} from "../lib/cart-purchase-deltas";
import { resolveProductImageUrl } from "../lib/category-placeholder";
import { fetchDispensaryMenu } from "../lib/fetch-dispensary-menu";
import { formatCentsAsUsd } from "../lib/format-menu";
import {
  cartSubtotalCents,
  formatReconcileSummary,
  lineSubtotalCents,
  useCartStore,
  type CartLine,
} from "../store/cart-store";

type Props = { dispensarySlug: string };

/**
 * Auto-dismiss window for the cart-screen feedback pill. The pill
 * exclusively renders ERRORS from `setLineQuantity` — most commonly
 * the Colorado daily-purchase-cap rejection message ("This pushes
 * your cart over the daily limit, ..."). 3s (the original value)
 * was too short for a regulatory message: the user has to read,
 * understand, and adjust their cart, all of which take longer than
 * a glance. 8s gives that window without feeling like sticky UI
 * noise.
 *
 * Other auto-dismiss timers in customer-web (`MenuScreen` /
 * `MarketplaceScreen` cart-add tips, `AccountAddressesScreen`
 * action feedback) stay at 3s — those are short success messages
 * where 3s is correct.
 */
const FEEDBACK_AUTODISMISS_MS = 8_000;

/** Lifted from CustomerShell — empty cart icon. */
function IconOrders({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 13V7a2 2 0 00-2-2H6a2 2 0 00-2 2v6m16 0v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );
}

export function CartScreen({ dispensarySlug }: Props) {
  const lines = useCartStore((s) => s.carts[dispensarySlug] ?? []);
  const setLineQuantity = useCartStore((s) => s.setLineQuantity);
  const removeLine = useCartStore((s) => s.removeLine);
  const clearCart = useCartStore((s) => s.clearCart);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [reconcileNotice, setReconcileNotice] = useState<string | null>(null);
  const [dispensaryLabel, setDispensaryLabel] = useState("");

  /**
   * Refetch the menu on mount so `reconcileCartWithMenu` can clamp/remove
   * lines that went stale between the last `MenuScreen` refetch and landing
   * here (fixes the 60 s stale-cap race). Also pulls the dispensary label
   * for the H1 — H1 starts as "Your cart" pre-fetch and gains the
   * "at {dispensaryLabel}" suffix once the fetch resolves (redline 04
   * flag 1; no slug fallback because slugs read like broken markup).
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const menu = await fetchDispensaryMenu(dispensarySlug);
        if (cancelled) return;
        const summary = useCartStore.getState().reconcileCartWithMenu(dispensarySlug, menu.items);
        const msg = formatReconcileSummary(summary);
        if (msg) setReconcileNotice(msg);
        const label = menu.dispensary.dbaName?.trim() || menu.dispensary.legalName;
        setDispensaryLabel(label);
      } catch {
        // Offline / API error is non-fatal here; the cart still shows the last snapshot.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispensarySlug]);

  /** Auto-dismiss pill matches the marketplace + menu cart-tip pattern. */
  useEffect(() => {
    if (!feedback) return;
    const id = window.setTimeout(() => setFeedback(null), FEEDBACK_AUTODISMISS_MS);
    return () => window.clearTimeout(id);
  }, [feedback]);

  const subtotal = useMemo(() => cartSubtotalCents(lines), [lines]);
  const overDailyCap = useMemo(() => {
    const deltas = purchaseDeltasFromCartLines(lines);
    return combinedPurchaseExceedsDailyCap(deltas);
  }, [lines]);
  const limitMessage = useMemo(() => {
    const deltas = purchaseDeltasFromCartLines(lines);
    return formatCombinedLimitExceededMessage(deltas);
  }, [lines]);

  function onQtyBlur(line: CartLine, raw: string) {
    const r = setLineQuantity(dispensarySlug, line.metrcPackageId, raw.trim());
    if (!r.ok) setFeedback(r.reason);
  }

  const heading = dispensaryLabel ? `Your cart at ${dispensaryLabel}` : "Your cart";

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-32 md:pb-16">
      <header className="space-y-2 border-b border-[var(--card-border)] pb-6">
        <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--green)]">
          Cart
        </p>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
            {heading}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <Link
              to="/$dispensarySlug/menu"
              params={{ dispensarySlug }}
              className="font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
            >
              ← Back to menu
            </Link>
            {lines.length > 0 ? (
              <button
                type="button"
                className="font-medium text-[var(--badge-red)] transition-colors hover:text-red-700"
                onClick={() => {
                  clearCart(dispensarySlug);
                  setFeedback(null);
                }}
              >
                Clear cart
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {feedback ? (
        <div
          role="status"
          className="inline-flex items-center rounded-full bg-amber-50 px-4 py-1.5 text-sm font-medium text-amber-900"
        >
          {feedback}
        </div>
      ) : null}

      {reconcileNotice ? (
        <div
          role="status"
          className="rounded-2xl border border-[var(--green-pale)] bg-[var(--surface-sage)] p-4 text-sm text-[var(--green-dark)]"
        >
          <p className="font-semibold">Cart updated to match available inventory</p>
          <p className="mt-1">{reconcileNotice}</p>
          <button
            type="button"
            className="mt-2 text-xs font-medium text-[var(--green-dark)] underline-offset-2 hover:underline"
            onClick={() => setReconcileNotice(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {overDailyCap ? (
        <div
          role="alert"
          className="rounded-2xl border border-[var(--badge-red)] bg-[var(--status-error-bg)] p-4 text-sm text-[var(--status-error-text)]"
        >
          <p className="font-semibold">Cart exceeds Colorado daily purchase limit</p>
          <p className="mt-1">{limitMessage}</p>
        </div>
      ) : lines.length > 0 ? (
        <p
          className="inline-flex items-center rounded-full bg-[var(--green-pale)] px-3 py-1 text-xs font-medium text-[var(--green-dark)]"
        >
          ✓ Within Colorado daily purchase limit
        </p>
      ) : null}

      {lines.length === 0 ? (
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-10 text-center shadow-cardMd">
          <IconOrders className="mx-auto h-12 w-12 text-[var(--text-muted)]" />
          <p className="mt-4 text-base font-semibold text-[var(--text-primary)]">
            Your cart is empty
          </p>
          <Link
            to="/$dispensarySlug/menu"
            params={{ dispensarySlug }}
            className="mt-4 inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
          >
            Browse the menu
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {lines.map((line) => (
            <CartLineItem
              key={line.metrcPackageId}
              line={line}
              onQtyBlur={onQtyBlur}
              onRemove={() => {
                removeLine(dispensarySlug, line.metrcPackageId);
                setFeedback(null);
              }}
            />
          ))}
        </ul>
      )}

      {lines.length > 0 ? (
        <CheckoutSummary
          dispensarySlug={dispensarySlug}
          subtotal={subtotal}
          overDailyCap={overDailyCap}
        />
      ) : null}
    </div>
  );
}

function CartLineItem({
  line,
  onQtyBlur,
  onRemove,
}: {
  line: CartLine;
  onQtyBlur: (line: CartLine, raw: string) => void;
  onRemove: () => void;
}) {
  const imgUrl = resolveProductImageUrl(line);
  return (
    <li className="flex flex-col gap-4 rounded-2xl border border-[var(--card-border)] bg-white p-4 shadow-cardMd sm:flex-row sm:items-center">
      <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl">
        <img
          src={imgUrl}
          alt=""
          loading="eager"
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[var(--text-primary)]">{line.productName}</p>
        <p className="mt-1 text-xs capitalize text-[var(--text-muted)]">
          {line.productCategory} · {line.quantityUnit} · max {line.maxQuantity}
        </p>
        <p className="mt-2 text-sm font-medium text-[var(--text-primary)]">
          {formatCentsAsUsd(line.unitPriceCents)}{" "}
          <span className="text-xs font-normal text-[var(--text-muted)]">each</span>
        </p>
      </div>
      <div className="flex items-center gap-3 sm:flex-col sm:items-end sm:gap-2">
        <label className="flex items-center gap-2 text-xs font-medium text-[var(--text-secondary)]">
          Qty
          <input
            key={line.quantity}
            type="text"
            inputMode="decimal"
            defaultValue={line.quantity}
            className="w-20 rounded-lg border border-[var(--card-border)] bg-white px-2 py-1.5 text-sm text-[var(--text-primary)] focus:border-[var(--green)] focus:outline-none focus:ring-1 focus:ring-[var(--green)]"
            onBlur={(e) => onQtyBlur(line, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              }
            }}
          />
        </label>
        <p className="text-sm font-semibold text-[var(--text-primary)]">
          {formatCentsAsUsd(lineSubtotalCents(line))}
        </p>
        <button
          type="button"
          className="text-xs font-medium text-[var(--badge-red)] transition-colors hover:text-red-700"
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
    </li>
  );
}

function CheckoutSummary({
  dispensarySlug,
  subtotal,
  overDailyCap,
}: {
  dispensarySlug: string;
  subtotal: number;
  overDailyCap: boolean;
}) {
  return (
    <>
      {/* Desktop / tablet — inline summary card. */}
      <div className="hidden border-t border-[var(--card-border)] pt-6 md:block">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <p className="text-base text-[var(--text-secondary)]">
            Subtotal{" "}
            <span className="ml-2 text-2xl font-bold text-[var(--text-primary)]">
              {formatCentsAsUsd(subtotal)}
            </span>
          </p>
          <div className="flex flex-col items-end gap-1">
            <CheckoutCta dispensarySlug={dispensarySlug} overDailyCap={overDailyCap} />
            {overDailyCap ? (
              <p className="text-xs text-[var(--badge-red)]">Reduce cart to continue</p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Mobile — sticky bar above the bottom-tabs. ~48 px tall content +
       *  padding = ~64 px total; combined with header + bottom-tabs the chrome
       *  stays under ~30 % of a 375 × 667 viewport. */}
      <div className="fixed inset-x-0 bottom-16 z-30 border-t border-[var(--card-border)] bg-white px-4 py-3 shadow-elevated md:hidden">
        <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-[var(--text-secondary)]">Subtotal</p>
            <p className="text-base font-bold text-[var(--text-primary)]">
              {formatCentsAsUsd(subtotal)}
            </p>
          </div>
          <div className="flex flex-col items-end">
            <CheckoutCta dispensarySlug={dispensarySlug} overDailyCap={overDailyCap} compact />
            {overDailyCap ? (
              <p className="mt-0.5 text-[10px] text-[var(--badge-red)]">Reduce cart to continue</p>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

function CheckoutCta({
  dispensarySlug,
  overDailyCap,
  compact,
}: {
  dispensarySlug: string;
  overDailyCap: boolean;
  compact?: boolean;
}) {
  /**
   * Tanstack `Link` doesn't support a true `disabled` prop, so the over-cap
   * state preserves the existing pattern: `aria-disabled` + `onClick`
   * `preventDefault()`. Functionally identical to a disabled button for users
   * (no navigation, AT announces disabled), keeps the visual consistent.
   */
  const baseCls = compact ? "px-4 py-2 text-sm" : "px-5 py-2.5 text-sm";
  return (
    <Link
      to="/$dispensarySlug/checkout"
      params={{ dispensarySlug }}
      className={
        overDailyCap
          ? `inline-flex items-center justify-center rounded-lg border border-[var(--card-border)] bg-white font-semibold text-[var(--text-muted)] ${baseCls} cursor-not-allowed`
          : `inline-flex items-center justify-center rounded-lg bg-[var(--green)] font-semibold text-white transition-colors hover:bg-[var(--green-dark)] ${baseCls}`
      }
      aria-disabled={overDailyCap}
      onClick={(e) => {
        if (overDailyCap) e.preventDefault();
      }}
    >
      Continue to checkout →
    </Link>
  );
}
